import * as React from 'react'
import {Drawer, Box, Typography, IconButton, Alert} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER} from '../../../styles/foundation/colors'
import KeyValue from './components/KeyValue'
import DwsPlate from './components/DwsPlate'
import {recordImageUrl, recordVideoUrl} from './components/DwsThumb'
import {kg as fmtKg, dims as fmtDims, date as fmtDate} from './format'
import {DWS_RETAIN} from './data/dwsCopy'

/**
 * One DWS record, in full.
 *
 * THREE cases, and the note above the readings must tell them apart. The
 * mockup only had two because every merchant record in it was made in-session
 * and therefore always had a real file. The frozen dataset has a third:
 * a merchant record that is genuinely on file with Prozo but whose actual
 * video and stills are not part of this dataset. Captioning that one "You
 * uploaded X in this session" would be a straightforward lie.
 *
 * `hasReal` covers BOTH media kinds (recordImageUrl OR recordVideoUrl) — a
 * merchant who uploads only a packing video and no stills in this session
 * (DWS_STEPS' own copy calls video the single strongest evidence on its own)
 * still gets the true "you uploaded this" caption. Gating on images alone
 * would tell the opposite lie: captioning a video just uploaded in this
 * session as "not part of this dataset."
 */
function sourceNote(record) {
  if (record.source !== 'upload') {
    return {
      severity: 'info',
      lead: `Sorter image, from ${record.courier}.`,
      body: 'This is not a photograph — Proship holds the scanner readings for this shipment, and the plate above prints them so the record stays legible without a file.',
    }
  }
  const imgUrl = recordImageUrl(record)
  const videoUrl = recordVideoUrl(record)
  if (imgUrl || videoUrl) {
    const name = videoUrl
      ? (record.video && record.video.name)
      : (record.images.find((i) => i.url) || {}).name
    return {
      severity: 'success',
      lead: 'Merchant evidence.',
      body: `You uploaded ${name} in this session. The readings below are the ones you entered with it.`,
    }
  }
  return {
    severity: 'warning',
    lead: 'Merchant evidence on file.',
    body: 'Prozo holds this record and the readings below, but the video and images themselves are not part of this dataset, so the plate above prints the readings instead. Nothing here is a photograph.',
  }
}

export default function DwsRecordDrawer({record, row, onClose}) {
  if (!record) return null

  const note = sourceNote(record)
  const url = recordImageUrl(record)
  const videoUrl = url ? null : recordVideoUrl(record)

  const items = [
    {label: 'AWB', value: record.awb},
    {label: 'Courier', value: record.courier},
    {label: 'Scanned on', value: fmtDate(record.date)},
    {label: 'Weight read', value: fmtKg(record.read)},
    {label: 'Dimensions read', value: fmtDims(record.dims)},
    {label: 'Volumetric from those dimensions', value: fmtKg(record.vol)},
  ]
  // Only courier records carry what the shipment was actually billed at.
  if (record.charged != null) items.push({label: 'Charged on this shipment', value: fmtKg(record.charged)})
  if (record.note) items.push({label: 'Merchant note', value: record.note})

  return (
    <Drawer anchor='right' open onClose={onClose} PaperProps={{sx: {width: {xs: '100%', sm: 560}, maxWidth: '100%'}}}>
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, borderBottom: `1px solid ${BORDER}`}}>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
            DWS record {record.awb}
          </Typography>
          <Typography sx={{fontFamily: 'monospace', fontSize: SIZE['2xs'], color: SUBTLE, wordBreak: 'break-word'}}>
            {row ? (row.sku || row.key) : ''}
          </Typography>
        </Box>
        <IconButton size='small' onClick={onClose} aria-label='Close record'><CloseIcon fontSize='small' /></IconButton>
      </Box>

      <Box sx={{p: 2, display: 'grid', gap: 2}}>
        <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden', bgcolor: NAVY}}>
          {url ? (
            <Box component='img' src={url} alt={`Merchant evidence you uploaded for AWB ${record.awb}`} sx={{display: 'block', width: '100%'}} />
          ) : videoUrl ? (
            <Box
              component='video'
              src={videoUrl}
              controls
              aria-label={`Merchant evidence video you uploaded for AWB ${record.awb}`}
              sx={{display: 'block', width: '100%'}}
            />
          ) : (
            <DwsPlate record={record} />
          )}
        </Box>

        <Alert severity={note.severity} sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}>
          <strong>{note.lead}</strong> {note.body}
        </Alert>

        <KeyValue items={items} />

        <Alert severity='warning' sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}>{DWS_RETAIN}</Alert>
      </Box>
    </Drawer>
  )
}
